import axios, { 
  AxiosInstance, 
  AxiosResponse, 
  AxiosError, 
  InternalAxiosRequestConfig 
} from 'axios'
import config from '@/config/env'
import * as Sentry from '@sentry/vue'
import { setSentryUserContext, setSentryTenantContext } from '@/plugins/sentry-context'

// Tipos para as respostas da API
export interface ApiResponse<T = any> {
  success: boolean
  data?: T
  message?: string
  errors?: Record<string, string[]>
}

export interface ApiError {
  message: string | string[]
  status?: number
  errors?: Record<string, string[]>
}

// Exportar instância singleton e classe
export class HttpClient {
  private instance: AxiosInstance

  constructor() {
    this.instance = axios.create({
      baseURL: config.apiBaseUrl,
      timeout: config.apiTimeout,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        // Headers obrigatórios conforme documentação da API
        // 'x-tenant-id': config.tenantId
      }
    })

    this.setupInterceptors()
  }

  /**
   * Configurar interceptors para requisições e respostas
   */
  private setupInterceptors(): void {
    // Interceptor para requisições
    this.instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Adicionar token de autenticação se disponível
        const token = localStorage.getItem('flowpilot_token')
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`
        }

        // Garantir que x-tenant-id esteja sempre presente (exceto para rotas de tenants)
        if (config.headers && !config.url?.includes('/tenants')) {
          const storedTenantId = localStorage.getItem('tenant_id')
          const headerTenantId = (config.headers as any)['x-tenant-id']
          const currentTenantId = storedTenantId || headerTenantId

          // Em produção multi-tenant por domínio, preferimos resolver tenant pelo Origin/Host no backend.
          // Só enviamos x-tenant-id quando existir no storage (ou já vier explicitamente no request).
          if (currentTenantId) (config.headers as any)['x-tenant-id'] = currentTenantId
          else delete (config.headers as any)['x-tenant-id']
        }

        // Se for FormData, remover Content-Type para o browser definir automaticamente com boundary
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type']
        }

        // Log da requisição em desenvolvimento
        if (import.meta.env.DEV) {
          console.log(`🚀 ${config.method?.toUpperCase()} ${config.url}`, {
            data: config.data,
            params: config.params,
            headers: config.headers
          })
        }

        return config
      },
      (error: AxiosError) => {
        console.error('❌ Request Error:', error)
        const requestId =
        error?.response?.headers?.['x-request-id'] ||
        error?.response?.headers?.['X-Request-Id'];
        Sentry.captureException(error, {
          tags: {
            module: 'http',
            status_code: String(error?.response?.status || 'unknown'),
          },
          extra: {
            url: error?.config?.url,
            method: error?.config?.method,
            requestId,
          },
        });
        return Promise.reject(error)
      }
    )

    // Interceptor para respostas
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        // Log da resposta em desenvolvimento
        if (import.meta.env.DEV) {
          console.log(`✅ ${response.config.method?.toUpperCase()} ${response.config.url}`, {
            status: response.status,
            data: response.data
          })
        }

        return response
      },
      async (error: AxiosError) => {
        // Log do erro em desenvolvimento
        if (import.meta.env.DEV) {
          console.error(`❌ ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
            status: error.response?.status,
            data: error.response?.data
          })
        }

        // Tratar erros de autenticação - só para endpoints que não são de refresh
        if (error.response?.status === 401 && !error.config?.url?.includes('/auth/refresh')) {
          const originalRequest = error.config

          // Tentar refresh do token
          try {
            const refreshToken = localStorage.getItem('flowpilot_refresh_token')
            if (refreshToken && originalRequest) {
              const refreshResponse = await this.instance.post('/auth/refresh', {
                refreshToken: refreshToken
              })

              // O backend retorna diretamente o accessToken
              const newToken = refreshResponse.data.accessToken || refreshResponse.data
              if (newToken) {
                localStorage.setItem('flowpilot_token', newToken)
                originalRequest.headers.Authorization = `Bearer ${newToken}`

                // Retentar a requisição original
                return this.instance.request(originalRequest)
              }
            }
          } catch (refreshError) {
            console.warn('⚠️ Token refresh failed:', refreshError)
          }

          // Sessão expirada: reportar e limpar contexto
          Sentry.captureException(error, {
            tags: { module: 'http', feature: 'auth', reason: 'session_expired' },
            extra: { url: error.config?.url, method: error.config?.method }
          })
          await this.handleAuthError()
        } else {
          // Erros de API (4xx/5xx) para diagnóstico
          const status = error.response?.status
          const requestId =
            error?.response?.headers?.['x-request-id'] ||
            error?.response?.headers?.['X-Request-Id']
          Sentry.captureException(error, {
            tags: {
              module: 'http',
              status_code: String(status ?? 'unknown'),
              feature: 'api_response'
            },
            extra: {
              url: error.config?.url,
              method: error.config?.method,
              requestId,
              responseData: error.response?.data
            }
          })
        }

        // Transformar erro para formato padronizado
        const apiError: ApiError = {
          message: this.getErrorMessage(error),
          status: error.response?.status,
          errors: this.getValidationErrors(error)
        }

        return Promise.reject(apiError)
      }
    )
  }

  /**
   * Formatar mensagem de erro para exibição ao usuário
   */
  static formatErrorMessage(error: ApiError): string {
    if (Array.isArray(error.message)) {
      // Se há múltiplas mensagens, juntá-las com quebra de linha
      return error.message.join('\n')
    }
    return error.message
  }

  /**
   * Obter primeira mensagem de erro (útil para exibições simples)
   */
  static getFirstErrorMessage(error: ApiError): string {
    if (Array.isArray(error.message)) {
      return error.message[0] || 'Erro desconhecido'
    }
    return error.message
  }

  /**
   * Tratar erros de autenticação (token expirado, etc.)
   */
  private async handleAuthError(): Promise<void> {
    // Limpar contexto Sentry (sessão inválida)
    setSentryUserContext(null)
    setSentryTenantContext(null)

    // Limpar dados de autenticação
    localStorage.removeItem('flowpilot_token')
    localStorage.removeItem('flowpilot_refresh_token')
    localStorage.removeItem('flowpilot_user')
    localStorage.removeItem('isAuthenticated')

    // Redirecionar para login se não estiver na página de auth
    if (!window.location.pathname.includes('/auth/')) {
      // Usar router ao invés de window.location para evitar reload completo
      const { default: router } = await import('@/router')
      router.push('/auth/sign-in')
    }
  }

  /**
   * Extrair mensagem de erro da resposta
   */
  private getErrorMessage(error: AxiosError): string | string[] {
    if (error.response?.data) {
      const data = error.response.data as any
      
      // Se message é um array, retornar como array
      if (Array.isArray(data.message)) {
        return data.message
      }
      
      // Se message é string, retornar como string
      if (typeof data.message === 'string') {
        return data.message
      }
      
      // Fallback para propriedade error
      if (data.error) {
        return Array.isArray(data.error) ? data.error : data.error
      }
    }

    switch (error.response?.status) {
      case 400:
        return 'Dados inválidos enviados'
      case 401:
        return 'Acesso não autorizado'
      case 403:
        return 'Acesso negado'
      case 404:
        return 'Recurso não encontrado'
      case 422:
        return 'Dados de validação inválidos'
      case 500:
        return 'Erro interno do servidor'
      default:
        return error.message || 'Erro de conexão'
    }
  }

  /**
   * Extrair erros de validação da resposta
   */
  private getValidationErrors(error: AxiosError): Record<string, string[]> | undefined {
    if (error.response?.data) {
      const data = error.response.data as any
      if (data.errors && typeof data.errors === 'object') {
        return data.errors
      }
    }
    return undefined
  }

  /**
   * Métodos HTTP públicos
   * A API retorna os dados diretamente, não em um wrapper ApiResponse
   */
  async get<T>(url: string, params?: any): Promise<T> {
    const response = await this.instance.get(url, { params })
    return response.data
  }

  async post<T>(url: string, data?: any): Promise<T> {
    const response = await this.instance.post(url, data)
    return response.data
  }

  async put<T>(url: string, data?: any): Promise<T> {
    const response = await this.instance.put(url, data)
    return response.data
  }

  async patch<T>(url: string, data?: any): Promise<T> {
    const response = await this.instance.patch(url, data)
    return response.data
  }

  async delete<T>(url: string): Promise<T> {
    const response = await this.instance.delete(url)
    return response.data
  }

  /**
   * Upload de arquivos
   */
  async upload<T>(url: string, file: File, onProgress?: (progress: number) => void): Promise<T> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await this.instance.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(progress)
        }
      }
    })

    return response.data
  }

  /**
   * Obter instância do axios para casos especiais
   */
  getInstance(): AxiosInstance {
    return this.instance
  }
}

// Exportar instância singleton
export const httpClient = new HttpClient()
export default httpClient